import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { resolveImg } from "@/lib/api";
import { money, CATEGORIES } from "@/lib/campaignMeta";
import OpportunityCard from "@/components/OpportunityCard";
import { RecJetMindCard } from "@/components/RecJetMindCard";
import MarineSearchDemand from "@/components/MarineSearchDemand";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Ship, CheckCircle2, Megaphone, Sparkles, ArrowRight, Anchor, Lightbulb, CalendarDays,
  MapPin, Radar, Users, DollarSign, PieChart, BarChart3, Facebook, Plug, AlertTriangle,
  Eye, Trophy, Activity, ChevronRight,
} from "lucide-react";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}
function fmtDate(d) {
  if (!d) return "";
  try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }); }
  catch { return d; }
}
function awayLabel(d) {
  if (!d) return "";
  const t = new Date(d + "T00:00:00");
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const n = Math.round((t - now) / 86400000);
  if (n === 0) return "Today";
  if (n > 0) return `${n} day${n === 1 ? "" : "s"} away`;
  return "In progress";
}

// KPI card. `awaiting` renders an honest not-connected state instead of a fabricated number.
const Kpi = ({ icon: Icon, label, value, sub, accent = "navy", awaiting, testid }) => {
  const tones = {
    navy: "bg-navy/5 text-navy", teal: "bg-teal-500/10 text-teal-600",
    green: "bg-emerald-500/10 text-emerald-600", gold: "bg-gold/15 text-gold",
    blue: "bg-blue-500/10 text-blue-600",
  };
  return (
    <div className="bg-white rounded-2xl border border-border p-5 card-hover" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</span>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tones[accent]}`}><Icon className="w-4 h-4" /></div>
      </div>
      {awaiting ? (
        <div className="flex items-center gap-1.5 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2">
          <Plug className="w-3.5 h-3.5" /> Awaiting Meta
        </div>
      ) : (
        <>
          <div className="font-serif text-2xl font-bold text-navy">{value}</div>
          {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
        </>
      )}
    </div>
  );
};

const PremierPartnerCard = ({ partner }) => {
  const inner = (
    <div className="relative h-full rounded-3xl overflow-hidden border border-gold/30 bg-navy flex flex-col" data-testid="dashboard-premier-partner">
      <div className="flex items-center gap-2 px-5 pt-4 pb-2">
        <span className="text-[10px] font-bold uppercase tracking-widest text-gold">RecJet Premier Partner</span>
      </div>
      <div className="flex-1 flex items-center justify-center p-4 pt-0">
        <div className="w-full aspect-[8/3] rounded-xl overflow-hidden bg-navy/60 flex items-center justify-center">
          <img src={resolveImg(partner.creative_url)} alt={partner.name || "Premier Partner"} className="w-full h-full object-contain" data-testid="premier-partner-creative" />
        </div>
      </div>
    </div>
  );
  if (partner.destination_url) {
    return <a href={partner.destination_url} target="_blank" rel="noopener noreferrer" className="block h-full transition-opacity hover:opacity-95" data-testid="premier-partner-link">{inner}</a>;
  }
  return inner;
};

const STATUS_TONE = {
  draft: "bg-slate-100 text-slate-600", created: "bg-slate-100 text-slate-600",
  approved: "bg-emerald-50 text-emerald-700", scheduled: "bg-blue-50 text-blue-700",
  published: "bg-emerald-50 text-emerald-700", paused: "bg-amber-50 text-amber-700",
  completed: "bg-slate-100 text-slate-600",
};
function campaignStatusLabel(c) {
  if (c.approval_status === "awaiting_approval") return "Awaiting approval";
  if (c.publishing_status === "published") return "Published (unverified)";
  if (c.publishing_status === "scheduled" || c.scheduled_date) return "Scheduled";
  if (c.approval_status === "approved") return "Approved";
  return "Created";
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [dealership, setDealership] = useState(null);
  const [opps, setOpps] = useState([]);
  const [evPrompt, setEvPrompt] = useState(null);
  const [learnCats, setLearnCats] = useState([]);
  const [partner, setPartner] = useState(null);
  const [budget, setBudget] = useState(null);
  const [metaConnected, setMetaConnected] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/dealership").then(({ data }) => setDealership(data)).catch(() => {});
    api.get("/opportunities").then(({ data }) => setOpps(data)).catch(() => {});
    api.get("/premier-partner/active").then(({ data }) => setPartner(data.partner || null)).catch(() => {});
    api.get("/meta/status").then(({ data }) => setMetaConnected(!!data.connected)).catch(() => setMetaConnected(false));
    api.get("/intelligence/decision-resolution")
      .then(({ data }) => setBudget((data.final || {}).budget_context || null)).catch(() => {});
    api.get("/intelligence/local-events").then(({ data }) => {
      setEvPrompt(data.event_learning_prompt || null);
      setLearnCats((data.event_learning_prompt?.suggested_categories) || []);
    }).catch(() => {});
  }, []);

  const toggleLearnCat = (cat) => setLearnCats((arr) => (arr.includes(cat) ? arr.filter((c) => c !== cat) : [...arr, cat]));
  const submitLearning = async (status) => {
    if (!evPrompt) return;
    try {
      await api.post(`/local-events/${evPrompt.event_id}/learning`, { status, boat_categories: status === "answered" ? learnCats : [] });
      toast.success(status === "ask_me_later" ? "No problem, we'll ask again closer to the event" : "Thanks, noted for this event");
      setEvPrompt(null);
    } catch { toast.error("Could not save your answer"); }
  };

  const topOpps = opps.slice(0, 5);
  const events = stats?.upcoming_events || [];
  const recent = stats?.recent_campaigns || [];
  const plannedBudget = budget?.total_planned_paid_budget;
  const remainingBudget = budget?.available_uncommitted_budget;
  const remainingPct = (plannedBudget && remainingBudget != null) ? Math.round((remainingBudget / plannedBudget) * 100) : null;

  // Needs Attention: real, actionable signals only.
  const attention = [];
  if (metaConnected === false) attention.push({ icon: Facebook, text: "Meta is not connected. Connect to launch and measure paid ads.", action: () => navigate("/settings"), cta: "Connect" });
  if (plannedBudget === 0) attention.push({ icon: DollarSign, text: "Monthly Media Plan is not configured for this month.", action: () => navigate("/settings"), cta: "Set budget" });
  if (opps.length > 0) attention.push({ icon: Lightbulb, text: `${opps.length} inventory ${opps.length === 1 ? "opportunity needs" : "opportunities need"} marketing attention.`, action: () => navigate("/opportunities"), cta: "Review" });

  return (
    <div className="space-y-8 fade-up" data-testid="dashboard-page">
      {/* Greeting + Premier Partner */}
      <div className={partner ? "grid lg:grid-cols-2 gap-6 items-stretch" : ""}>
        <div className="relative overflow-hidden rounded-3xl navy-gradient p-8 sm:p-10">
          <img src="https://images.unsplash.com/photo-1787988012442-7af67643dc8d?crop=entropy&cs=srgb&fm=jpg&q=85" alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
          {dealership?.logo && (
            <div className="relative z-20 mb-4 inline-flex sm:mb-0 sm:absolute sm:top-6 sm:right-6 bg-white rounded-xl px-4 py-3 shadow-md" data-testid="dashboard-logo">
              <img src={resolveImg(dealership.logo)} alt={dealership?.name || "logo"} className="max-w-[170px] max-h-[64px] w-auto h-auto object-contain" />
            </div>
          )}
          <div className={`relative z-10 ${dealership?.logo ? "sm:pr-[210px]" : ""}`}>
            <p className="text-gold text-xs font-bold uppercase tracking-widest mb-2">Welcome back</p>
            <h1 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-2" data-testid="dashboard-greeting">
              {greeting()}, {dealership?.name || "Dealer"}
            </h1>
            <p className="text-slate-300 mb-6">Turn your inventory into more buyers with data driven marketing.</p>
            <Button onClick={() => navigate("/create-campaign")} className="gold-gradient text-navy font-bold h-12 px-6 gold-sheen" data-testid="dashboard-create-campaign-btn">
              <Sparkles className="w-4 h-4 mr-2" /> Create New Campaign
            </Button>
          </div>
          {/* inline mini-stats strip like the mockup */}
          <div className="relative z-10 mt-7 flex flex-wrap gap-x-8 gap-y-3 text-white/90">
            <div className="flex items-center gap-2"><Ship className="w-4 h-4 text-gold" /><span className="font-bold">{stats?.total_boats ?? "-"}</span><span className="text-xs text-slate-300">Boats in inventory</span></div>
            <div className="flex items-center gap-2"><Megaphone className="w-4 h-4 text-gold" /><span className="font-bold">{stats?.campaigns_created ?? "-"}</span><span className="text-xs text-slate-300">Campaigns created</span></div>
            <div className="flex items-center gap-2"><DollarSign className="w-4 h-4 text-gold" /><span className="font-bold">{stats ? money(stats.total_value) : "-"}</span><span className="text-xs text-slate-300">Total inventory value</span></div>
          </div>
        </div>
        {partner && <PremierPartnerCard partner={partner} />}
      </div>

      {/* RecJet Mind Command Center (existing engine, unchanged) */}
      <RecJetMindCard />

      {/* Event Learning prompt */}
      {evPrompt && (
        <div className="rounded-3xl border border-navy/20 bg-navy/[0.03] p-6 sm:p-7" data-testid="event-learning-prompt">
          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-9 h-9 rounded-lg bg-navy flex items-center justify-center"><Radar className="w-4 h-4 text-gold" /></div>
            <div>
              <h2 className="font-serif text-xl font-bold text-navy">RecJet Mind is learning about {evPrompt.name}</h2>
              <p className="text-sm text-muted-foreground">{evPrompt.question}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-4" data-testid="learning-category-chips">
            {CATEGORIES.map((cat) => {
              const on = learnCats.includes(cat);
              return (
                <button key={cat} type="button" onClick={() => toggleLearnCat(cat)}
                  data-testid={`learning-cat-${cat.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${on ? "bg-navy text-white border-navy" : "bg-white text-navy border-slate-300 hover:border-navy"}`}>
                  {cat}
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-2 mt-5">
            <Button onClick={() => submitLearning("answered")} disabled={!learnCats.length} className="bg-navy hover:bg-navy-light text-white h-10" data-testid="learning-save-btn">Save these categories</Button>
            <Button variant="outline" onClick={() => submitLearning("no_history")} className="h-10" data-testid="learning-no-history-btn">Not sure / no history yet</Button>
            <Button variant="ghost" onClick={() => submitLearning("ask_me_later")} className="h-10 text-muted-foreground" data-testid="learning-ask-later-btn">Ask me later</Button>
          </div>
        </div>
      )}

      {/* Today's Snapshot */}
      <section data-testid="dashboard-snapshot">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-9 h-9 rounded-lg gold-gradient flex items-center justify-center"><BarChart3 className="w-4 h-4 text-navy" /></div>
          <div>
            <h2 className="font-serif text-2xl font-bold text-navy">Today's Snapshot</h2>
            <p className="text-sm text-muted-foreground">Key metrics from your inventory, campaigns, and buyer activity.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <Kpi icon={Ship} label="Total Inventory" value={stats ? `${stats.total_boats} Boats` : "-"} sub={stats ? `${money(stats.total_value)} value` : ""} accent="navy" testid="kpi-total-inventory" />
          <Kpi icon={CheckCircle2} label="Active Listings" value={stats ? `${stats.active_boats} Boats` : "-"} sub="In stock & ready" accent="teal" testid="kpi-active-listings" />
          <Kpi icon={Megaphone} label="Campaigns Created" value={stats ? stats.campaigns_created : "-"} sub="All time" accent="blue" testid="kpi-campaigns" />
          <Kpi icon={Users} label="Leads This Month" awaiting accent="green" testid="kpi-leads" />
          <Kpi icon={DollarSign} label="Monthly Media Budget" value={plannedBudget != null ? (plannedBudget > 0 ? money(plannedBudget) : "Not set") : "-"} sub={plannedBudget > 0 ? "Planned spend" : "Configure in Settings"} accent="gold" testid="kpi-media-budget" />
          <Kpi icon={PieChart} label="Remaining Budget" value={remainingBudget != null ? money(remainingBudget) : "-"} sub={remainingPct != null ? `${remainingPct}% remaining` : ""} accent="green" testid="kpi-remaining-budget" />
        </div>
      </section>

      {/* Top Opportunities + Upcoming Events */}
      <div className="grid gap-6 lg:grid-cols-3 min-w-0">
        <section className="lg:col-span-2 min-w-0" data-testid="dashboard-opportunities">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg gold-gradient flex items-center justify-center"><Lightbulb className="w-4 h-4 text-navy" /></div>
              <div>
                <h2 className="font-serif text-xl font-bold text-navy">Top Opportunities</h2>
                <p className="text-sm text-muted-foreground">Boats with the highest marketing potential right now.</p>
              </div>
            </div>
            {opps.length > 0 && (
              <Button variant="ghost" onClick={() => navigate("/opportunities")} className="text-gold" data-testid="view-all-opportunities">
                View all ({opps.length}) <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
          {topOpps.length ? (
            <div className="grid gap-4 sm:grid-cols-2">
              {topOpps.slice(0, 4).map((o) => <OpportunityCard key={o.id} opp={o} />)}
            </div>
          ) : (
            <div className="text-center py-10 text-muted-foreground bg-white rounded-2xl border border-border">
              <Lightbulb className="w-9 h-9 mx-auto mb-2 opacity-40" />
              You are all caught up. Opportunities appear as inventory ages, prices change and events approach.
            </div>
          )}
        </section>

        <div className="bg-white rounded-2xl border border-border p-6 h-fit" data-testid="dashboard-events">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg gold-gradient flex items-center justify-center"><CalendarDays className="w-4 h-4 text-navy" /></div>
              <div>
                <h2 className="font-serif text-xl font-bold text-navy">Upcoming Events</h2>
                <p className="text-sm text-muted-foreground">Local shows and events that can drive buyer traffic.</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate("/calendar")} className="text-gold" data-testid="view-calendar">View all</Button>
          </div>
          {events.length ? (
            <div className="space-y-4">
              {events.map((e) => (
                <div key={e.id} className="flex items-center gap-3" data-testid={`dashboard-event-${e.id}`}>
                  <div className="w-10 h-10 rounded-lg bg-navy/5 flex items-center justify-center shrink-0"><CalendarDays className="w-4 h-4 text-navy" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-navy text-sm truncate">{e.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{fmtDate(e.start_date)}{e.location ? ` · ${e.location}` : ""}</div>
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gold bg-gold/10 px-2 py-0.5 rounded-full shrink-0">{awayLabel(e.start_date)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-muted-foreground text-sm"><MapPin className="w-7 h-7 mx-auto mb-2 opacity-40" />No events planned. Add one in the Calendar.</div>
          )}
        </div>
      </div>

      {/* Active Campaigns + Performance Snapshot */}
      <div className="grid gap-6 lg:grid-cols-3 min-w-0">
        <section className="lg:col-span-2 min-w-0 bg-white rounded-2xl border border-border overflow-hidden" data-testid="active-campaigns">
          <div className="flex items-center justify-between p-5 pb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg gold-gradient flex items-center justify-center"><Megaphone className="w-4 h-4 text-navy" /></div>
              <h2 className="font-serif text-xl font-bold text-navy">Active Campaigns</h2>
            </div>
            <Button variant="ghost" onClick={() => navigate("/campaigns")} className="text-gold" data-testid="view-all-campaigns">View all <ArrowRight className="w-4 h-4 ml-1" /></Button>
          </div>
          {recent.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary/60 text-navy">
                  <tr className="text-left">
                    <th className="px-5 py-2.5 font-semibold">Campaign</th>
                    <th className="px-3 py-2.5 font-semibold">Type</th>
                    <th className="px-3 py-2.5 font-semibold">Inventory</th>
                    <th className="px-3 py-2.5 font-semibold">Status</th>
                    <th className="px-3 py-2.5 font-semibold">Performance</th>
                    <th className="px-3 py-2.5" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {recent.map((c) => {
                    const bs = c.boat_snapshot || {};
                    const label = campaignStatusLabel(c);
                    const tone = STATUS_TONE[label.toLowerCase().split(" ")[0]] || "bg-slate-100 text-slate-600";
                    return (
                      <tr key={c.id} className="hover:bg-secondary/40" data-testid={`campaign-row-${c.id}`}>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-3">
                            <img src={resolveImg(c.selected_photos?.[0] || bs.photos?.[0])} alt="" className="w-11 h-11 rounded-lg object-cover bg-secondary shrink-0" />
                            <div className="min-w-0">
                              <div className="font-semibold text-navy truncate max-w-[180px]">{c.title}</div>
                              <div className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3"><span className="text-xs font-semibold text-gold bg-gold/10 px-2 py-0.5 rounded-full whitespace-nowrap">{c.objective_label}</span></td>
                        <td className="px-3 py-3 text-navy whitespace-nowrap max-w-[150px] truncate">{[bs.year, bs.manufacturer, bs.model].filter(Boolean).join(" ") || "Inventory"}</td>
                        <td className="px-3 py-3"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${tone}`}>{label}</span></td>
                        <td className="px-3 py-3"><span className="inline-flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 whitespace-nowrap"><Plug className="w-3 h-3" /> Awaiting Meta</span></td>
                        <td className="px-3 py-3 text-right">
                          <button onClick={() => navigate("/campaigns")} className="text-xs font-semibold text-primary inline-flex items-center gap-0.5" data-testid={`campaign-view-${c.id}`}>View <ChevronRight className="w-3.5 h-3.5" /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground"><Anchor className="w-10 h-10 mx-auto mb-3 opacity-40" />No campaigns yet. Create your first one to see it here.</div>
          )}
        </section>

        <div className="bg-white rounded-2xl border border-border p-6" data-testid="performance-snapshot">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-9 h-9 rounded-lg bg-navy/5 flex items-center justify-center"><BarChart3 className="w-4 h-4 text-navy" /></div>
            <h2 className="font-serif text-lg font-bold text-navy">Performance Snapshot</h2>
          </div>
          <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50/60 p-5 text-center" data-testid="performance-awaiting">
            <Plug className="w-8 h-8 mx-auto text-amber-500 mb-2" />
            <div className="font-semibold text-navy">Advertising reporting not connected</div>
            <p className="text-xs text-muted-foreground mt-1">Impressions, reach, clicks, engagement, leads and spend will appear here once Meta is connected and verified.</p>
            <Button onClick={() => navigate("/settings")} variant="outline" className="mt-3 h-9" data-testid="performance-connect-btn">Go to Settings</Button>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4">
            {[{ i: Users, l: "Leads Generated" }, { i: Activity, l: "Engagement" }, { i: Eye, l: "Inventory Views" }, { i: Trophy, l: "Top Category" }].map((m) => (
              <div key={m.l} className="rounded-xl border border-border p-3">
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold"><m.i className="w-3.5 h-3.5" /> {m.l}</div>
                <div className="text-sm text-slate-400 mt-1">Not available</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Marine Search Demand Intelligence (new) */}
      <MarineSearchDemand />

      {/* Needs Attention */}
      <section data-testid="needs-attention">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center"><AlertTriangle className="w-4 h-4 text-amber-600" /></div>
          <h2 className="font-serif text-xl font-bold text-navy">Needs Attention</h2>
        </div>
        {attention.length ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {attention.map((a, i) => (
              <div key={i} className="bg-white rounded-2xl border border-border p-4 flex items-start gap-3" data-testid={`attention-item-${i}`}>
                <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center shrink-0"><a.icon className="w-4 h-4 text-amber-600" /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-navy">{a.text}</p>
                  <button onClick={a.action} className="text-xs font-semibold text-primary mt-1.5 inline-flex items-center gap-0.5" data-testid={`attention-cta-${i}`}>{a.cta} <ChevronRight className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground bg-white rounded-2xl border border-border" data-testid="attention-empty">
            <CheckCircle2 className="w-9 h-9 mx-auto mb-2 opacity-40" />
            Nothing needs your attention right now.
          </div>
        )}
      </section>
    </div>
  );
}
